import { Button, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useState, type FC } from 'react'
import { useTranslation } from 'react-i18next'

import { createUserDetailsQueryKey, type UserUpdatePayloadDto } from '@hcr/api/consumer'
import { Alert, ButtonLoading } from '@hcr/ui'
import { logger } from '@hcr/utils'

import { useIdToken, useUserUpdateMutation } from '../../../../hooks'
import { Path } from '../../../../models'
import { to } from '../../../../utils'
import { LayoutAccountEdit } from '../common'

import { AccountEditEmailSuccess } from './AccountEditEmailSuccess'

const BACK_NAVIGATION_PATH = to(Path.AccountEdit)

interface AccountEditEmailConfirmationProps {
  accountEditFromData: UserUpdatePayloadDto
  setShowEmailConfirmation: (showEmailConfirmation: boolean) => void
}

export const AccountEditEmailConfirmation: FC<AccountEditEmailConfirmationProps> = ({
  accountEditFromData,
  setShowEmailConfirmation,
}: AccountEditEmailConfirmationProps) => {
  const { t } = useTranslation()
  const idToken = useIdToken()

  const queryClient = useQueryClient()
  const userUpdate = useUserUpdateMutation({ idToken: String(idToken) })

  const [isFormSubmitted, setIsFormSubmitted] = useState(false)

  if (isFormSubmitted) {
    return <AccountEditEmailSuccess />
  }

  const onSubmit = async () => {
    try {
      await userUpdate.mutateAsync(accountEditFromData)

      queryClient.removeQueries({ queryKey: createUserDetailsQueryKey() })
      setIsFormSubmitted(true)
    } catch {
      logger.error('Failed to edit the user:', accountEditFromData)
    }
  }

  return (
    <LayoutAccountEdit
      backNavigationPath={BACK_NAVIGATION_PATH}
      onClickBackNavigation={() => setShowEmailConfirmation(false)}
      headline={
        <Typography component='h1' variant='headlineL'>
          {t('account-edit.confirm-new-email-address')}
        </Typography>
      }
    >
      <Typography component='h2' variant='bodyL'>
        {t('account-edit.your-new-email-address-will-become-your-user-id-and-will-be-updated-to-your-customer-info')}
      </Typography>

      {userUpdate.isError && (
        <Alert
          severity='error'
          title={t('account-edit.changing-email-error')}
          description={t(
            'account-edit.changing-your-email-address-was-not-successful-make-sure-that-your-email-address-is-not-already-in-use-with-holiday-club-services'
          )}
        />
      )}

      <ButtonLoading onClick={onSubmit} isLoading={userUpdate.isPending} variant='contained' color='success'>
        {t('account-edit.confirm')}
      </ButtonLoading>

      <Button
        onClick={() => setShowEmailConfirmation(false)}
        disabled={userUpdate.isPending}
        variant='outlined'
        color='black'
      >
        {t('account-edit.cancel')}
      </Button>
    </LayoutAccountEdit>
  )
}
